import React, {useMemo, useState} from "react";
import {InputAdornment, ListItemText, ListSubheader} from "@mui/material";
import {sortBy} from "lodash";
import {Box, Checkbox, Chip, Colors, FormControl, IconSearch, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../../ui/components";
import {Option} from "../../../../apis/requests/segments/types";
import {BaseValueInputProps} from "./types";

type Props = BaseValueInputProps & {
  options: Option[];
  multi: boolean;
};

const SEARCH_MIN_OPTIONS = 10;

const labelFor = (o: Option): string => (o.key ? `${o.value} (${o.key})` : o.value);
const keyFor = (o: Option): string => o.key ?? o.value;

const EnumValueInput: React.FC<Props> = ({value, valueError, onChange, options, multi, showLabel = true}) => {
  const [search, setSearch] = useState("");
  const showSearch = options.length > SEARCH_MIN_OPTIONS;

  const sortedOptions = useMemo(() => sortBy(options, (o) => labelFor(o).toLowerCase()), [options]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter((o) => labelFor(o).toLowerCase().includes(q));
  }, [sortedOptions, search]);

  const visibleOptions = showSearch ? filteredOptions : sortedOptions;

  const labelForValue = (v: string): string => {
    const opt = options.find((o) => o.value === v);
    return opt ? labelFor(opt) : v;
  };

  const searchHeader = (
    <ListSubheader sx={{py: 1, bgcolor: "background.paper"}}>
      <TextField
        autoFocus
        size="small"
        placeholder="Search…"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconSearch />
              </InputAdornment>
            ),
          },
        }}
      />
    </ListSubheader>
  );

  const menuProps = {
    PaperProps: {style: {maxHeight: 400}},
    autoFocus: false,
  } as const;

  if (multi) {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Box sx={{flex: 2}}>
        {showLabel && (
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Value
          </Text>
        )}
        <FormControl size="small" fullWidth error={!!valueError}>
          <Select
            multiple
            fullWidth
            displayEmpty
            value={arr}
            onChange={(e) => onChange(typeof e.target.value === "string" ? [e.target.value] : e.target.value)}
            renderValue={(selected) => (
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                {(selected as string[]).map((v) => (
                  <Chip key={v} size_="small" variant_="blue" label={labelForValue(v)} />
                ))}
              </Box>
            )}
            MenuProps={menuProps}
          >
            {showSearch && searchHeader}
            {visibleOptions.map((o) => (
              <MenuItem key={keyFor(o)} value={o.value}>
                <Checkbox checked={arr.includes(o.value)} />
                <ListItemText primary={labelFor(o)} />
              </MenuItem>
            ))}
            {showSearch && visibleOptions.length === 0 && (
              <MenuItem disabled value="__no_match__">
                No matches
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box sx={{flex: 2}}>
      {showLabel && (
        <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
          Value
        </Text>
      )}
      <FormControl size="small" fullWidth error={!!valueError}>
        <Select
          fullWidth
          displayEmpty
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          renderValue={(selected) => labelForValue(selected as string)}
          MenuProps={menuProps}
        >
          {showSearch && searchHeader}
          {visibleOptions.map((o) => (
            <MenuItem key={keyFor(o)} value={o.value}>
              {labelFor(o)}
            </MenuItem>
          ))}
          {showSearch && visibleOptions.length === 0 && (
            <MenuItem disabled value="__no_match__">
              No matches
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default EnumValueInput;
